import React, {
  ReactNode,
  SyntheticEvent,
  useState,
  useEffect,
  useContext,
  memo,
} from 'react';
import { PhoneType } from 'types';
import {
  MenuItem,
  SxProps,
  InputAdornment,
  Stack,
  Divider,
  Typography,
} from '@mui/material';
import { SelectValidator } from 'react-material-ui-form-validator';
import { AppContext } from 'context/appContext';
import { AUTURA_GRAYSCALE, formatPhoneNumber, parsePhoneNumber } from 'utils';
import AuturaTextField from './AuturaTextField';

export interface AuturaPhoneFieldProps {
  name: string;
  label: string;
  phoneNumber: string;
  setPhoneNumber?: (value: string) => void;
  readonly?: boolean;
  required?: boolean;
  margin?: 'none' | 'dense' | 'normal';
  variant?: 'standard' | 'outlined' | 'filled';
  initialPhone?: string;
  sx?: SxProps;
  resolve?: Promise<object>;
  // phone type props
  typeFieldLabel?: string;
  typeFieldName?: string;
  phoneType?: PhoneType;
  setPhoneType?: (value: PhoneType) => void;
  typeFieldReadonly?: boolean;
}

const AuturaPhoneField = ({
  name,
  label,
  phoneNumber,
  margin = 'none',
  variant = 'outlined',
  setPhoneNumber,
  typeFieldName,
  typeFieldLabel,
  phoneType,
  setPhoneType,
  typeFieldReadonly = false,
  readonly = false,
  required = false,
  sx,
  resolve,
  initialPhone,
}: AuturaPhoneFieldProps): ReactNode => {
  const { state } = useContext(AppContext);
  const { mainNumber, extension } = parsePhoneNumber(phoneNumber);
  const [inputValue, setInputValue] = useState(formatPhoneNumber(mainNumber));
  const [extensionValue, setExtensionValue] = useState(extension);

  // Sync from parent prop (e.g. external reset)
  useEffect(() => {
    const { mainNumber: newMainNumber, extension: newExtension } =
      parsePhoneNumber(phoneNumber);
    if (inputValue.replace(/\D/g, '') !== newMainNumber) {
      setInputValue(formatPhoneNumber(newMainNumber));
    }
    if (extensionValue !== newExtension) {
      setExtensionValue(newExtension);
    }
  }, [phoneNumber]);

  const pushToParent = (main: string, ext: string) => {
    if (setPhoneNumber) {
      const cleanMainNumber = main.replace(/\D/g, '');
      const fullNumber = ext ? `${cleanMainNumber}x${ext}` : cleanMainNumber;
      setPhoneNumber(fullNumber);
    }
  };

  const handleInput = (event: React.FormEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(event.currentTarget.value);
    setInputValue(formatted);
    pushToParent(formatted, extensionValue);
  };

  const handleExtensionInput = (event: React.FormEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value.replace(/\D/g, '');
    setExtensionValue(value);
    pushToParent(inputValue, value);
  };

  const handlePhoneType = (event: SyntheticEvent) => {
    const newPhoneType = (event.target as HTMLInputElement).value as PhoneType;
    if (setPhoneType) {
      setPhoneType(newPhoneType);
    }

    // Clear extension if switching to mobile
    if (newPhoneType === PhoneType.Mobile && extensionValue) {
      setExtensionValue('');
      pushToParent(inputValue, '');
    }
  };

  const isValidPhone = `matchRegexp:${'\\D*(1)?\\D*([2-9][0-8]\\d)\\D*([2-9]\\d{2})\\D*(\\d{4})\\D*\\d*'}`;
  const withType = typeFieldName && typeFieldLabel;
  const isMobilePhone = phoneType === PhoneType.Mobile;
  const withExtension = !!withType && !isMobilePhone; // Show extension only if it's not a mobile phone
  const hasEndAdornment = !!withType || withExtension;

  return (
    <AuturaTextField
      sx={sx}
      label={label}
      value={inputValue}
      margin={margin}
      variant={variant}
      name={name}
      onChange={handleInput}
      validators={[isValidPhone]}
      errorMessages={['Invalid phone number']}
      resolve={resolve}
      initialValue={initialPhone}
      readonly={readonly}
      required={required}
      shrink={hasEndAdornment || undefined}
      notched={hasEndAdornment || undefined}
      endAdornment={
        hasEndAdornment && (
          <InputAdornment position='end'>
            <Stack
              direction='row'
              justifyContent='flex-end'
              alignItems='center'
            >
              {withExtension && (
                <AuturaTextField
                  name={`${name}_extension`}
                  value={extensionValue}
                  onChange={handleExtensionInput}
                  variant='standard'
                  margin='none'
                  readonly={readonly}
                  disableUnderline
                  startAdornment={
                    <InputAdornment position='start'>
                      <Typography variant='subtitle2' color='text.secondary'>
                        {'ext.'}
                      </Typography>
                    </InputAdornment>
                  }
                  sx={{
                    width: 80,
                    mt: '4px',
                  }}
                />
              )}
              {withType && (
                <>
                  <Divider
                    orientation='vertical'
                    flexItem
                    sx={{
                      my: -0.5,
                      mr: 1,
                      ml: withExtension ? 1 : 0,
                      borderColor: AUTURA_GRAYSCALE[400],
                    }}
                  />
                  <SelectValidator
                    name={typeFieldName ? typeFieldName : ''}
                    value={phoneType || PhoneType.None}
                    sx={{ textTransform: 'capitalize', width: 95 }}
                    placeholder={typeFieldLabel}
                    readOnly={readonly || typeFieldReadonly}
                    fullWidth={false}
                    variant={'standard'}
                    margin={'none'}
                    validators={required ? ['required'] : []}
                    errorMessages={[`${typeFieldLabel} is required`]}
                    onChange={handlePhoneType}
                    InputProps={{
                      disableUnderline: true,
                    }}
                  >
                    {state.phoneTypes.map((p) => (
                      <MenuItem
                        key={p}
                        value={`${p}`}
                        sx={{ textTransform: 'capitalize' }}
                      >
                        {p}
                      </MenuItem>
                    ))}
                  </SelectValidator>
                </>
              )}
            </Stack>
          </InputAdornment>
        )
      }
    />
  );
};

export default memo(AuturaPhoneField);
